import {
  flattenToSingleLine,
  PAGE_CONTEXT_PREAMBLE_SEPARATOR,
  stripContextEnvelope,
  stripPageContextPreamble,
} from './flora-page-context-preamble';

/**
 * Stable leading phrase every session-context preamble begins with. Used both
 * to build the preamble and to strip it back off persisted messages on
 * history reload, so the two never drift.
 */
const SESSION_CONTEXT_PREAMBLE_PREFIX = 'The user is currently viewing an agent session';

/**
 * Fixed closing sentence of every session-context preamble. Paired with the
 * separator it forms the exact envelope boundary the stripper cuts at, so the
 * cut lands past the whole preamble rather than at the first blank line inside
 * it, and a user message that merely happens to open with the prefix is left
 * alone unless it also reproduces this closing sentence verbatim.
 */
const SESSION_CONTEXT_PREAMBLE_SUFFIX = '. Use this as context for their questions.';

/**
 * Shape a session id must have before it is allowed into an agent prompt.
 *
 * The id arrives from `useParams`, which returns the URL-DECODED path segment —
 * so any byte an attacker can percent-encode into that segment would otherwise
 * land inside the preamble, ahead of the user's own instruction, in a link that
 * needs no credentials to deliver. (The session-detail page mounts the Flora
 * dock even on its error branch, so an id that resolves to nothing still arms
 * the chat.) Real ids are server-generated identifiers, so anything outside
 * this charset is rejected outright rather than sanitised.
 */
const SAFE_SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The full generated envelope boundary: the preamble's closing sentence
 * immediately followed by the separator that `applyPageContextPreamble` joins
 * with. `indexOf` on this literal is what makes stripping independent of
 * anything the interpolated session fields contain — the builder flattens the
 * assembled sentence, so no field can reproduce the separator and forge it.
 */
const SESSION_CONTEXT_PREAMBLE_BOUNDARY = `${SESSION_CONTEXT_PREAMBLE_SUFFIX}${PAGE_CONTEXT_PREAMBLE_SEPARATOR}`;

/** Matches `(session_id: …)` at the end of the text preceding a boundary. */
const SESSION_ID_ANCHOR = /\(session_id: [^()]*\)$/;

/**
 * Builds the context preamble that seeds Flora with the Agent Miner session
 * currently on screen, so a chat opened from the session detail view knows
 * which session is being viewed.
 *
 * Carries the session id and nothing else, deliberately:
 *
 * 1. The id comes from the route, so it is available on the first render. The
 *    Flora button is live while the session request is still in flight and
 *    injection happens on the FIRST turn only with no retry, so a preamble
 *    derived from fetched data is lost forever for a user who opens Flora and
 *    types before the response lands.
 * 2. The id is what the agent's org-scoped session tool takes as an argument,
 *    so title, status and source are all reachable from it — and reachable
 *    fresh, rather than frozen at up-to-`gcTime`-old values.
 * 3. It keeps free text out of the prompt. A session title is attacker-writable
 *    (`session.name` is client-supplied hook data) and would sit, undelimited,
 *    immediately before the user's own instruction.
 *
 * The result is still flattened: a route param is URL-decoded, so `%0A%0A` in
 * the path would otherwise reproduce the envelope separator inside the
 * preamble and forge the boundary the stripper cuts at.
 *
 * @param {string} sessionId - The session id from the route.
 * @returns {string | undefined} The single-line preamble sentence (no trailing
 *   separator), or `undefined` when the id is not a plausible session id — the
 *   caller then seeds no context rather than a poisoned one.
 */
function buildSessionContextPreamble(sessionId: string): string | undefined {
  if (!SAFE_SESSION_ID_PATTERN.test(sessionId)) return undefined;
  // Kept as a second line of defence even though the charset guard above
  // already excludes whitespace: if that pattern is ever loosened, flattening
  // still stops an id from forging the envelope separator.
  return flattenToSingleLine(
    `${SESSION_CONTEXT_PREAMBLE_PREFIX} (session_id: ${sessionId})${SESSION_CONTEXT_PREAMBLE_SUFFIX}`,
  );
}

/**
 * Recovers the user's typed text from a persisted message that may carry an
 * injected session-context preamble. The backend stores the raw outgoing
 * message (preamble included), so history reload would otherwise render the
 * preamble in the user's first bubble.
 *
 * Cuts at the full generated envelope — prefix at the start AND the closing
 * sentence followed by a blank line — rather than at the first blank line, so
 * a multi-line title cannot leave preamble text behind and an ordinary message
 * that merely starts with the prefix is returned untouched. Returns the content
 * unchanged when no preamble is present, so it is safe to run over every
 * historical user message.
 *
 * @param {string} content - A persisted user-message string.
 * @returns {string} The content with any leading session-context preamble removed.
 */
function stripSessionContextPreamble(content: string): string {
  return stripContextEnvelope(
    content,
    SESSION_CONTEXT_PREAMBLE_PREFIX,
    SESSION_CONTEXT_PREAMBLE_BOUNDARY,
    SESSION_ID_ANCHOR,
  );
}

/**
 * Removes whichever injected context preamble a persisted message carries.
 *
 * A message can only ever carry ONE injected preamble — the surface that sent
 * it chose one — so the two strippers must not be composed in series. Running
 * both lets a page-preamble message whose user text happens to contain a
 * session preamble (a block pasted from another chat, say) lose that text too.
 * The page envelope is tried first; the session stripper only sees content the
 * page stripper declined to touch.
 *
 * @param {string} content - A persisted user-message string.
 * @returns {string} The content with at most one leading context preamble removed.
 */
function stripContextPreamble(content: string): string {
  const withoutPagePreamble = stripPageContextPreamble(content);
  return withoutPagePreamble === content ? stripSessionContextPreamble(content) : withoutPagePreamble;
}

export {
  buildSessionContextPreamble,
  SESSION_CONTEXT_PREAMBLE_PREFIX,
  stripContextPreamble,
  stripSessionContextPreamble,
};
